import { useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import useSWR from 'swr'
import { UploadIcon } from '@radix-ui/react-icons'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { toast } from '@/components/ui/use-toast'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'
import { layerIcon } from 'commons/cdn'
import { useUI } from 'Editor'
import {
  importParsedPsd,
  planPsdImport,
  zOffsetForAppend,
  type ExistingLayer,
  type ImportProgress,
} from './importPsd'
import {
  outputPlaceForPsd,
  parsePsdFile,
  psdPlaceError,
  type LayerIconHit,
  type PsdParseResult,
} from './parsePsd'

const matchLabel = (match: PsdParseResult['folders'][0]['match']) => {
  if (match === 'hit') return '已对上'
  if (match === 'ambiguous') return '重名，按未匹配'
  return '未匹配'
}

const ImportPsdDialog = () => {
  const { tid } = useParams<{ tid: string }>()
  const toggleLayer = useUI((state) => state.toggleLayer)
  const { data: existingLayers } = useSWR<ExistingLayer[]>(
    tid ? ['/layers', { tid }] : null,
  )
  const existingLayerCount = (existingLayers || []).filter(
    (layer) => layer.id && layer.id !== 'draft',
  ).length
  const { data: template } = useSWR<{ fullscreenType?: number }>(
    tid ? [`/templates/${tid}`] : null,
  )
  const { data: icons } = useSWR<LayerIconHit[]>([
    '/layericons',
    { author: 'admin', limit: 1000 },
  ])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [open, setOpen] = useState(false)
  const [parsing, setParsing] = useState(false)
  const [importing, setImporting] = useState(false)
  const [error, setError] = useState('')
  const [fileName, setFileName] = useState('')
  const [parsed, setParsed] = useState<PsdParseResult | null>(null)
  const [progress, setProgress] = useState<ImportProgress | null>(null)
  const [replaceExisting, setReplaceExisting] = useState(false)

  const reset = () => {
    setParsed(null)
    setError('')
    setFileName('')
    setParsing(false)
    setImporting(false)
    setProgress(null)
    setReplaceExisting(false)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const onOpenChange = (next: boolean) => {
    if (!next && importing) return
    if (!next) reset()
    setOpen(next)
  }

  const handlePick = async (file?: File) => {
    if (!file) return
    setParsing(true)
    setError('')
    setFileName(file.name)
    setOpen(true)
    try {
      const result = await parsePsdFile(file, icons || [])
      setParsed(result)
    } catch (err) {
      setParsed(null)
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setParsing(false)
    }
  }

  const zOffset = zOffsetForAppend(existingLayers || [], replaceExisting)
  const plans = parsed
    ? planPsdImport(parsed.folders, existingLayers || [], replaceExisting)
    : []
  const newLayerCount = plans.filter((plan) => plan.action === 'create').length
  const reusedLayerCount = new Set(
    plans.map((plan) => plan.lid).filter((lid): lid is string => Boolean(lid)),
  ).size
  const place = parsed
    ? outputPlaceForPsd(parsed.width, parsed.height)
    : null
  const portrait = Boolean(place && (place.offsetX !== 0 || place.offsetY !== 0))
  const placeError = parsed
    ? psdPlaceError(place, template?.fullscreenType)
    : null
  const canvasOk = Boolean(place) && !placeError
  const canvasHint = !parsed
    ? ''
    : placeError
      ? placeError
      : place?.fullscreenType === 3
        ? '3:4，按 1536×2048 原样导入'
        : portrait
          ? '竖屏，居中放入 2048×2048，左右留白'
          : '2048，可以导入'
  const canImport =
    Boolean(tid && parsed && canvasOk && parsed.folders.length > 0 && !importing)

  const handleImport = async () => {
    if (!tid || !parsed || !canImport) return
    setImporting(true)
    try {
      let firstLid = ''
      const result = await importParsedPsd(
        tid,
        parsed,
        existingLayers || [],
        (lid) => {
          if (!firstLid) firstLid = lid
        },
        setProgress,
        replaceExisting,
      )
      if (firstLid) toggleLayer(firstLid)
      const uploaded = `${result.componentCount} 个组件正在上传。请等层列表旁进度走完再离开页面。`
      const description = result.reusedCount
        ? result.layerCount
          ? `新建 ${result.layerCount} 层，并向 ${result.reusedCount} 个已有层追加组件，共 ${uploaded}`
          : `向 ${result.reusedCount} 个已有层追加组件，共 ${uploaded}`
        : `${result.layerCount} 层、${uploaded}`
      toast({
        title: '已开始后台上传',
        description,
      })
      setImporting(false)
      reset()
      setOpen(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
      setImporting(false)
    }
  }

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".psd"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0]
          void handlePick(file)
        }}
      />
      <Button
        variant="outline"
        onClick={() => fileInputRef.current?.click()}
        disabled={parsing || !icons}
      >
        <UploadIcon className="mr-2 h-4 w-4" />
        {parsing ? '解析中...' : '导入 PSD'}
      </Button>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="flex max-h-[85vh] max-w-3xl flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle>导入 PSD</DialogTitle>
            <DialogDescription>
              {fileName || '选择按层分好组的 PSD。组名对 layerIcon，叠放听 PSD。'}
            </DialogDescription>
          </DialogHeader>

          {parsing ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              正在解析，大文件可能要几秒…
            </div>
          ) : null}

          {error ? (
            <div className="text-sm text-destructive">{error}</div>
          ) : null}

          {importing && progress ? (
            <div className="space-y-3 py-4">
              <div className="text-sm">{progress.label}</div>
              <div className="h-2 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-primary transition-all duration-200"
                  style={{
                    width: `${Math.max(
                      6,
                      Math.round(
                        (progress.current / Math.max(progress.total, 1)) * 100,
                      ),
                    )}%`,
                  }}
                />
              </div>
              <div className="text-xs text-muted-foreground">
                {progress.current} / {progress.total}
              </div>
              <div className="rounded-md border border-amber-300/70 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                弹窗里先把层建完，建好后才开始传组件图。请等层列表旁的进度走完，再退出或刷新当前页面。
              </div>
            </div>
          ) : null}

          {parsed && !importing ? (
            <div className="min-h-0 flex-1 space-y-4 overflow-hidden text-sm">
              {existingLayerCount > 0 ? (
                <div className="space-y-3">
                  <RadioGroup
                    value={replaceExisting ? 'replace' : 'append'}
                    onValueChange={(value) =>
                      setReplaceExisting(value === 'replace')
                    }
                    className="gap-3"
                  >
                    <div className="flex items-start space-x-3 rounded-md border px-3 py-2">
                      <RadioGroupItem value="append" id="psd-append" className="mt-0.5" />
                      <Label htmlFor="psd-append" className="font-normal leading-snug">
                        保留已有 {existingLayerCount} 层。组名和已有层名字相同就追加组件，对不上就新建层。
                      </Label>
                    </div>
                    <div className="flex items-start space-x-3 rounded-md border border-destructive/40 px-3 py-2">
                      <RadioGroupItem value="replace" id="psd-replace" className="mt-0.5" />
                      <Label htmlFor="psd-replace" className="font-normal leading-snug text-destructive">
                        删除本模板全部现有层和组件，再按这份 PSD 重建。不可撤销。
                      </Label>
                    </div>
                  </RadioGroup>
                  {!replaceExisting && plans.length > 0 ? (
                    <div className="text-muted-foreground">
                      {reusedLayerCount > 0 && newLayerCount > 0
                        ? `${reusedLayerCount} 个同名层会追加组件。将新建 ${newLayerCount} 层，zIndex 从 ${zOffset} 起。`
                        : reusedLayerCount > 0
                          ? `${reusedLayerCount} 个同名层会追加组件。`
                          : `将新建 ${newLayerCount} 层，zIndex 从 ${zOffset} 起。`}
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="text-muted-foreground">
                  当前没有层，将按 PSD 新建。
                </div>
              )}
              <div className="rounded-md border bg-muted/50 px-3 py-2 text-muted-foreground">
                确认后先处理层：组名和已有层相同就追加组件，对不上就新建。层处理完才开始上传组件图。请等上传进度走完，再退出或刷新当前页面。
              </div>
              <div>
                画布 {parsed.width}×{parsed.height}
                <span
                  className={
                    canvasOk
                      ? 'ml-2 text-muted-foreground'
                      : 'ml-2 text-destructive'
                  }
                >
                  （{canvasHint}）
                </span>
              </div>

              {parsed.folders.length === 0 ? (
                <div className="text-destructive">
                  没有可用的顶层文件夹。根目录散图层会被忽略。
                </div>
              ) : (
                <div className="min-h-0 max-h-[min(50vh,28rem)] overflow-auto rounded-md border">
                  <table className="w-full text-left">
                    <thead className="sticky top-0 z-10 bg-muted">
                      <tr>
                        <th className="px-3 py-2 font-medium">层（文件夹）</th>
                        <th className="px-3 py-2 font-medium">组件</th>
                        <th className="px-3 py-2 font-medium">layerIcon</th>
                        <th className="px-3 py-2 font-medium">导入</th>
                        <th className="px-3 py-2 font-medium">z</th>
                        <th className="px-3 py-2 font-medium">生成 HD Cover</th>
                      </tr>
                    </thead>
                    <tbody>
                      {plans.map((plan) => (
                        <tr key={`${plan.folder.name}-${plan.folder.zIndex}`} className="border-t">
                          <td className="px-3 py-2">{plan.folder.name}</td>
                          <td className="px-3 py-2">{plan.folder.images.length}</td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              {plan.folder.icon?.code || plan.folder.icon?.id ? (
                                <img
                                  src={layerIcon(plan.folder.icon.code || plan.folder.icon.id)}
                                  alt=""
                                  className="h-8 w-8 rounded object-contain"
                                />
                              ) : null}
                              <span
                                className={
                                  plan.folder.match === 'hit'
                                    ? 'text-muted-foreground'
                                    : 'text-destructive'
                                }
                              >
                                {plan.folder.match === 'hit'
                                  ? plan.folder.icon?.name || plan.folder.name
                                  : matchLabel(plan.folder.match)}
                              </span>
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            {plan.action === 'append' && plan.lid
                              ? `追加到「${plan.layerName}」`
                              : plan.action === 'append'
                                ? `并入「${plan.layerName}」`
                                : '新建层'}
                          </td>
                          <td className="px-3 py-2">{plan.zIndex}</td>
                          <td className="px-3 py-2">
                            {plan.folder.images.filter((item) => !item.fillsCanvas).length}
                            /{plan.folder.images.length}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {parsed.skippedRoot.length > 0 ? (
                <div className="text-muted-foreground">
                  根目录散图层已忽略：{parsed.skippedRoot.join('、')}
                </div>
              ) : null}
              {parsed.skippedEmpty.length > 0 ? (
                <div className="text-muted-foreground">
                  空文件夹已忽略：{parsed.skippedEmpty.join('、')}
                </div>
              ) : null}
              {parsed.skippedNoPixels.length > 0 ? (
                <div className="text-muted-foreground">
                  无像素层已忽略：{parsed.skippedNoPixels.join('、')}
                </div>
              ) : null}
            </div>
          ) : null}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={importing}
            >
              取消
            </Button>
            <Button onClick={() => void handleImport()} disabled={!canImport}>
              {importing
                ? progress
                  ? `${progress.current}/${progress.total}`
                  : '准备中...'
                : existingLayerCount > 0 && replaceExisting
                  ? '删除现有层并导入'
                  : existingLayerCount > 0 && newLayerCount === 0
                    ? '追加到已有层'
                    : existingLayerCount > 0
                      ? '追加导入'
                      : '确认导入'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default ImportPsdDialog
